// Source-first дерево экспорта Workspace.
// См. docs/prd/09_workspace_impexp.md §5 (вариант 3 обсуждения).
//
// Загружает dependency_graph (backend собирает граф от страниц вниз к источникам).
// Рендерит source → templates → components (через template и прямые) + shortcut на страницы.
// Синхронизирует выбор в hidden inputs selection[sources][], [item_templates][], [components][], [pages][]
// при submit формы экспорта.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "loadBtn", "inventoryIndicator", "inventoryIndicatorText",
    "mainGrid", "emptyState", "groupsPane", "groupsList", "totalsCard", "detailPane",
    "selectionBar", "selectionCount", "selectionBreakdown"
  ]
  static values = { graphUrl: String }

  connect() {
    this.graph = null
    this.expanded = new Set() // "source:code", "template:code"
    this.selected = {
      sources: new Set(),
      item_templates: new Set(),
      components: new Set(),
      pages: new Set()
    }
    this.query = ""

    const form = this.element.closest("form") || this.element.querySelector("form")
    form?.addEventListener("submit", this._injectHiddenInputs.bind(this))
    this._form = form
  }

  // ───────────────────────────────────────────── LOAD

  async loadGraph(event) {
    event.preventDefault()
    if (this.hasLoadBtnTarget) {
      this.loadBtnTarget.disabled = true
      this._loadBtnOriginal = this.loadBtnTarget.innerHTML
      this.loadBtnTarget.innerHTML = "Загрузка..."
    }
    try {
      const res = await fetch(this.graphUrlValue, { headers: { Accept: "application/json" } })
      const data = await res.json()
      if (!data.success) {
        alert((data.errors || ["Ошибка загрузки графа"]).join(", "))
        return
      }
      this.graph = data
      this._seedDefaults()
      this._render()
      this._showIndicator()
    } catch (e) {
      alert(`Ошибка: ${e.message}`)
    } finally {
      if (this.hasLoadBtnTarget) {
        this.loadBtnTarget.disabled = false
        this.loadBtnTarget.innerHTML = this._loadBtnOriginal
      }
    }
  }

  _seedDefaults() {
    // По умолчанию отмечаем всё (пользователь снимает ненужное).
    this.selected.sources = new Set((this.graph.sources || []).map(s => s.code))
    this.selected.item_templates = new Set((this.graph.item_templates || []).map(t => t.code))
    this.selected.components = new Set((this.graph.components || []).map(c => c.code))
    this.selected.pages = new Set((this.graph.pages || []).map(p => p.code))
    // Раскроем по-умолчанию все source-узлы.
    this.expanded = new Set((this.graph.sources || []).map(s => `source:${s.code}`))
  }

  _showIndicator() {
    if (!this.hasInventoryIndicatorTarget) return
    const s = this.graph
    this.inventoryIndicatorTextTarget.textContent =
      `${s.sources.length} источников · ${s.item_templates.length} шаблонов · ${s.components.length} компонентов`
    this.inventoryIndicatorTarget.classList.remove("hidden")
    this.inventoryIndicatorTarget.classList.add("inline-flex")
  }

  // ───────────────────────────────────────────── RENDER

  _render() {
    this.emptyStateTarget.classList.add("hidden")
    this.groupsPaneTarget.classList.remove("hidden")
    this.groupsPaneTarget.classList.add("flex", "flex-col")
    this.detailPaneTarget.classList.remove("hidden")
    this.detailPaneTarget.classList.add("flex")

    this._renderGroupsPane()
    this._renderDetail()
    this._updateSelectionBar()
  }

  _renderGroupsPane() {
    // Слева — шортки: сводка + страницы (чекбокс на странице = toggle всех её компонентов).
    const selectedSrc = this.selected.sources.size
    const totalSrc = this.graph.sources.length
    const selectedTpl = this.selected.item_templates.size
    const totalTpl = this.graph.item_templates.length
    const selectedCmp = this.selected.components.size
    const totalCmp = this.graph.components.length

    let html = `
      <div class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Итог</div>
      <div class="mb-3 rounded-[10px] border border-[#E6E8EC] bg-white p-2.5 text-[12px] leading-5">
        <div class="flex justify-between"><span class="text-[#6B7280]">Источники</span><span class="font-mono">${selectedSrc}/${totalSrc}</span></div>
        <div class="flex justify-between"><span class="text-[#6B7280]">Шаблоны</span><span class="font-mono">${selectedTpl}/${totalTpl}</span></div>
        <div class="flex justify-between"><span class="text-[#6B7280]">Компоненты</span><span class="font-mono">${selectedCmp}/${totalCmp}</span></div>
      </div>

      <div class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Страницы (шорткат)</div>
      <div class="flex flex-col gap-0.5">
    `
    for (const page of this.graph.pages || []) {
      const state = this._pageState(page)
      const icon = state === "checked" ? "☑" : (state === "indeterminate" ? "◪" : "☐")
      const selCount = (page.component_codes || []).filter(c => this.selected.components.has(c)).length
      html += `
        <button type="button"
                data-action="click->dae20--workspace-export-tree#togglePage"
                data-page-code="${this._esc(page.code)}"
                class="flex items-center gap-2 w-full rounded-[6px] px-2 py-1.5 text-[12.5px] text-left hover:bg-white/70">
          <span class="w-4 text-center">${icon}</span>
          <span class="flex-1 truncate">${this._esc(page.name || page.code)}</span>
          <span class="font-mono text-[10.5px] text-[#9AA1AC]">${selCount}/${page.component_codes.length}</span>
        </button>
      `
    }
    html += `</div>`
    this.groupsListTarget.innerHTML = html
  }

  _renderDetail() {
    let html = `
      <div class="sticky top-0 z-10 bg-white border-b border-[#E6E8EC] px-4 py-2.5">
        <input type="search"
               value="${this._esc(this.query)}"
               placeholder="Поиск по коду или имени источника/шаблона/компонента…"
               data-action="input->dae20--workspace-export-tree#onSearch"
               class="h-[30px] w-full px-3 border border-[#E6E8EC] rounded-[7px] bg-[#FBFBFD] text-[12.5px] outline-none"/>
      </div>
      <div class="flex-1 overflow-y-auto px-2 py-2">
    `

    const sources = this._filteredSources()
    if (sources.length === 0) {
      html += `<div class="p-6 text-center text-[#9AA1AC] text-[12.5px]">Ничего не найдено</div>`
    } else {
      for (const src of sources) html += this._renderSource(src)
    }

    html += `</div>`
    this.detailPaneTarget.innerHTML = html
  }

  _renderSource(src) {
    const id = `source:${src.code}`
    const state = this._sourceState(src)
    const isExp = this.expanded.has(id)
    const templates = (src.used_by_templates || []).map(c => this.graph.item_templates.find(t => t.code === c)).filter(Boolean)
    const directComps = (src.used_by_components_direct || []).map(c => this.graph.components.find(x => x.code === c)).filter(Boolean)
    const totalChildren = templates.length + directComps.length

    let html = `
      <div class="mb-1 rounded-[8px] border border-[#E6E8EC] bg-white">
        <div class="flex items-center gap-2 px-3 py-2">
          <button type="button"
                  data-action="click->dae20--workspace-export-tree#toggleExpand"
                  data-node-id="${id}"
                  class="w-4 text-[12px] text-[#6B7280] ${totalChildren === 0 ? 'invisible' : ''}">${isExp ? "▾" : "▸"}</button>
          ${this._checkbox(state, `toggleSource`, { sourceCode: src.code })}
          <span class="flex items-center gap-2 flex-1 min-w-0">
            <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#EEF2FF] text-[#3D5AFE] text-[10px] font-bold uppercase tracking-wide">source</span>
            <span class="text-[13px] font-semibold truncate">${this._esc(src.name || src.code)}</span>
            <span class="font-mono text-[11px] text-[#9AA1AC] truncate">${this._esc(src.code)}</span>
          </span>
          <span class="font-mono text-[10.5px] text-[#9AA1AC]">${totalChildren}</span>
        </div>
    `

    if (isExp && totalChildren > 0) {
      html += `<div class="border-t border-[#F0F1F4] pl-7 pr-2 py-1.5 bg-[#FBFBFD]">`
      for (const tpl of templates) html += this._renderTemplate(tpl)
      for (const c of directComps) html += this._renderComponent(c, { direct: true })
      html += `</div>`
    }
    html += `</div>`
    return html
  }

  _renderTemplate(tpl) {
    const id = `template:${tpl.code}`
    const state = this._templateState(tpl)
    const isExp = this.expanded.has(id)
    const comps = (tpl.used_by_components || []).map(c => this.graph.components.find(x => x.code === c)).filter(Boolean)

    let html = `
      <div class="mb-0.5">
        <div class="flex items-center gap-2 py-1">
          <button type="button"
                  data-action="click->dae20--workspace-export-tree#toggleExpand"
                  data-node-id="${id}"
                  class="w-4 text-[12px] text-[#6B7280] ${comps.length === 0 ? 'invisible' : ''}">${isExp ? "▾" : "▸"}</button>
          ${this._checkbox(state, `toggleTemplate`, { templateCode: tpl.code })}
          <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#FEF3C7] text-[#B45309] text-[10px] font-bold uppercase tracking-wide">template</span>
          <span class="text-[12.5px] font-semibold truncate">${this._esc(tpl.name || tpl.code)}</span>
          <span class="font-mono text-[11px] text-[#9AA1AC] truncate">${this._esc(tpl.code)}</span>
          <span class="font-mono text-[10.5px] text-[#9AA1AC] ml-auto">${comps.length}</span>
        </div>
    `

    if (isExp && comps.length > 0) {
      html += `<div class="pl-6">`
      for (const c of comps) html += this._renderComponent(c, { direct: false })
      html += `</div>`
    }
    html += `</div>`
    return html
  }

  _renderComponent(c, { direct }) {
    const checked = this.selected.components.has(c.code)
    const state = checked ? "checked" : "unchecked"
    return `
      <div class="flex items-center gap-2 py-0.5">
        <span class="w-4"></span>
        ${this._checkbox(state, `toggleComponent`, { componentCode: c.code })}
        <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#ECFCCB] text-[#4D7C0F] text-[10px] font-bold uppercase tracking-wide">component</span>
        <span class="text-[12px] font-semibold truncate">${this._esc(c.name || c.code)}</span>
        <span class="font-mono text-[10.5px] text-[#9AA1AC] truncate">${this._esc(c.code)}</span>
        ${c.template_code ? `<span class="inline-flex items-center px-1.5 h-[16px] rounded bg-[#F3F4F6] text-[#6B7280] font-mono text-[10px]">${this._esc(c.template_code)}</span>` : ""}
        ${direct ? `<span class="text-[9.5px] text-[#6B7280] bg-[#F3F4F6] rounded px-1.5 py-0.5">direct</span>` : ""}
        <span class="ml-auto text-[10.5px] text-[#9AA1AC]">page: <span class="font-mono">${this._esc(c.page_code || "—")}</span></span>
      </div>
    `
  }

  _checkbox(state, action, dataset) {
    const dataAttrs = Object.entries(dataset).map(([k, v]) => `data-${this._kebab(k)}="${this._esc(v)}"`).join(" ")
    const indeterminate = state === "indeterminate"
    const checked = state === "checked"
    return `
      <input type="checkbox"
             ${checked ? "checked" : ""}
             ${indeterminate ? 'data-indeterminate="1"' : ""}
             ${dataAttrs}
             data-action="change->dae20--workspace-export-tree#${action}"
             class="w-[14px] h-[14px] cursor-pointer"
             style="accent-color:#3D5AFE;"/>
    `
  }

  _kebab(s) { return s.replace(/([A-Z])/g, "-$1").toLowerCase() }
  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  // Выставляем indeterminate после того как DOM обновлён.
  _applyIndeterminates() {
    this.detailPaneTarget.querySelectorAll('input[type=checkbox][data-indeterminate="1"]').forEach(el => {
      el.indeterminate = true
    })
  }

  _filteredSources() {
    const q = this.query.trim().toLowerCase()
    if (!q) return this.graph.sources || []
    const match = s => (s.code + " " + (s.name || "")).toLowerCase().includes(q)
    return (this.graph.sources || []).filter(src => {
      if (match(src)) return true
      const tpls = (src.used_by_templates || []).map(c => this.graph.item_templates.find(t => t.code === c)).filter(Boolean)
      if (tpls.some(t => match(t))) return true
      const cmps = [
        ...(src.used_by_components_direct || []).map(c => this.graph.components.find(x => x.code === c)).filter(Boolean),
        ...tpls.flatMap(t => (t.used_by_components || []).map(c => this.graph.components.find(x => x.code === c)).filter(Boolean))
      ]
      return cmps.some(c => match(c))
    })
  }

  // ───────────────────────────────────────────── STATE

  _sourceState(src) {
    // Source считается "checked" если сам в выбранных И все его дети-компоненты/шаблоны тоже
    const tplCodes = src.used_by_templates || []
    const directCmpCodes = src.used_by_components_direct || []
    const allTplsChecked = tplCodes.every(c => this.selected.item_templates.has(c))
    const allDirectCmpsChecked = directCmpCodes.every(c => this.selected.components.has(c))
    const selfChecked = this.selected.sources.has(src.code)
    const anyChildChecked =
      tplCodes.some(c => this.selected.item_templates.has(c)) ||
      directCmpCodes.some(c => this.selected.components.has(c))

    if (selfChecked && allTplsChecked && allDirectCmpsChecked) return "checked"
    if (selfChecked || anyChildChecked) return "indeterminate"
    return "unchecked"
  }

  _templateState(tpl) {
    const cmps = tpl.used_by_components || []
    const allChecked = cmps.every(c => this.selected.components.has(c))
    const anyChecked = cmps.some(c => this.selected.components.has(c))
    const self = this.selected.item_templates.has(tpl.code)
    if (self && allChecked) return "checked"
    if (self || anyChecked) return "indeterminate"
    return "unchecked"
  }

  _pageState(page) {
    const cmps = page.component_codes || []
    if (cmps.length === 0) return this.selected.pages.has(page.code) ? "checked" : "unchecked"
    const checked = cmps.filter(c => this.selected.components.has(c)).length
    if (checked === 0) return "unchecked"
    if (checked === cmps.length && this.selected.pages.has(page.code)) return "checked"
    return "indeterminate"
  }

  // ───────────────────────────────────────────── ACTIONS

  onSearch(e) {
    this.query = e.target.value || ""
    this._renderDetail()
    this._applyIndeterminates()
  }

  toggleExpand(e) {
    const id = e.currentTarget.dataset.nodeId
    if (this.expanded.has(id)) this.expanded.delete(id); else this.expanded.add(id)
    this._renderDetail()
    this._applyIndeterminates()
  }

  toggleSource(e) {
    const code = e.currentTarget.dataset.sourceCode
    const src = this.graph.sources.find(s => s.code === code)
    if (!src) return
    const turnOn = e.currentTarget.checked
    this._setSource(src, turnOn)
    this._rerender()
  }

  _setSource(src, on) {
    if (on) this.selected.sources.add(src.code); else this.selected.sources.delete(src.code)
    for (const tc of src.used_by_templates || []) {
      const tpl = this.graph.item_templates.find(t => t.code === tc)
      if (tpl) this._setTemplate(tpl, on)
    }
    for (const cc of src.used_by_components_direct || []) this._setComponent(cc, on)
  }

  toggleTemplate(e) {
    const code = e.currentTarget.dataset.templateCode
    const tpl = this.graph.item_templates.find(t => t.code === code)
    if (!tpl) return
    this._setTemplate(tpl, e.currentTarget.checked)
    this._rerender()
  }

  _setTemplate(tpl, on) {
    if (on) this.selected.item_templates.add(tpl.code); else this.selected.item_templates.delete(tpl.code)
    for (const cc of tpl.used_by_components || []) this._setComponent(cc, on)
  }

  toggleComponent(e) {
    this._setComponent(e.currentTarget.dataset.componentCode, e.currentTarget.checked)
    this._rerender()
  }

  _setComponent(code, on) {
    if (on) {
      this.selected.components.add(code)
      const comp = this.graph.components.find(c => c.code === code)
      if (comp?.page_code) this.selected.pages.add(comp.page_code)
    } else {
      this.selected.components.delete(code)
    }
  }

  togglePage(e) {
    const code = e.currentTarget.dataset.pageCode
    const page = this.graph.pages.find(p => p.code === code)
    if (!page) return
    const state = this._pageState(page)
    const turnOn = state !== "checked"
    if (turnOn) this.selected.pages.add(page.code); else this.selected.pages.delete(page.code)
    for (const cc of page.component_codes || []) this._setComponent(cc, turnOn)
    this._rerender()
  }

  clearSelection() {
    this.selected.sources.clear()
    this.selected.item_templates.clear()
    this.selected.components.clear()
    this.selected.pages.clear()
    this._rerender()
  }

  invertSelection() {
    const flip = (set, all) => {
      const next = new Set()
      for (const x of all) if (!set.has(x)) next.add(x)
      return next
    }
    this.selected.sources = flip(this.selected.sources, this.graph.sources.map(s => s.code))
    this.selected.item_templates = flip(this.selected.item_templates, this.graph.item_templates.map(t => t.code))
    this.selected.components = flip(this.selected.components, this.graph.components.map(c => c.code))
    this.selected.pages = flip(this.selected.pages, this.graph.pages.map(p => p.code))
    this._rerender()
  }

  _rerender() {
    this._renderGroupsPane()
    this._renderDetail()
    this._applyIndeterminates()
    this._updateSelectionBar()
  }

  _updateSelectionBar() {
    if (!this.hasSelectionBarTarget) return
    const total = this.selected.sources.size + this.selected.item_templates.size + this.selected.components.size + this.selected.pages.size
    this.selectionBarTarget.classList.remove("hidden")
    this.selectionBarTarget.classList.add("flex")
    this.selectionCountTarget.textContent = total
    this.selectionBreakdownTarget.innerHTML = `
      <span>${this.selected.sources.size} sources</span>
      <span>·</span><span>${this.selected.item_templates.size} templates</span>
      <span>·</span><span>${this.selected.components.size} components</span>
      <span>·</span><span>${this.selected.pages.size} pages</span>
    `
  }

  // ───────────────────────────────────────────── SUBMIT

  _injectHiddenInputs(_event) {
    if (!this.graph) return // карта не открыта — шлём как есть (экспорт всего)
    const form = this._form || this.element.querySelector("form")
    if (!form) return

    // Удалим прошлые hidden selection[*]
    form.querySelectorAll('input[data-tree-selection="1"]').forEach(n => n.remove())

    const add = (name, value) => {
      const inp = document.createElement("input")
      inp.type = "hidden"
      inp.name = name
      inp.value = value
      inp.dataset.treeSelection = "1"
      form.appendChild(inp)
    }

    const pairs = [
      ["sources", this.selected.sources],
      ["item_templates", this.selected.item_templates],
      ["components", this.selected.components],
      ["pages", this.selected.pages]
    ]
    for (const [key, set] of pairs) {
      add(`selection[${key}][]`, "") // маркер «группа была в UI»
      for (const v of set) add(`selection[${key}][]`, v)
    }
  }
}
